import { BlitzPage } from "@blitzjs/auth"
import { useParam } from "@blitzjs/next"
import { SField } from "@superwall/legacy-ui"
import { Switch } from "@superwall/ui"
import { useEnsureSession } from "app/authv2/use-session"
import { LayoutModal } from "app/core/components/layout/Modal"
import { PublicBetaFeatures, enabled, Feature } from "app/core/features"
import ApplicationSettingsV2 from "app/core/layouts/ApplicationSettingsV2"
import { trpc } from "app/core/trpcClient"
import { useRouter } from "next/navigation"
import { TryNewDashboardButton } from "app/core/components/NewDashboardExperienceSwitcher"
import { buildDashboardWeb2ApplicationUrl } from "app/dashboardWeb2/registry"
import {
  useApplicationSetting,
  useUpdateApplicationSetting,
} from "app/core/hooks/useApplicationSetting"

const ApplicationExperiments = () => {
  const router = useRouter()
  const applicationId = useParam("applicationId", "number")!
  const { data: application, refetch } = trpc.applications.getApplication.useQuery(
    { applicationId },
    { suspense: false }
  )
  const toggleFeatureMutation = trpc.experiments.toggleFeature.useMutation({
    onSuccess: () => {
      refetch()
      setTimeout(() => {
        router.refresh()
      }, 100)
    },
  })

  // Get project and find Stripe application
  const { data: projectData } = trpc.projects.getProjectByApplicationId.useQuery(
    { applicationId },
    { suspense: false }
  )

  // Check if there's a Stripe application in the project
  const stripeApplicationId = projectData?.stripeApplication?.id
  const hasStripeApplication = !!stripeApplicationId

  const {
    data: enableStripeMobileCheckout,
    refetch: refetchStripeSetting,
    isLoading: isLoadingStripeSetting,
  } = useApplicationSetting(stripeApplicationId || applicationId, "enable_stripe_mobile_checkout")
  const { mutateAsync: updateStripeSetting } = useUpdateApplicationSetting(
    stripeApplicationId || applicationId,
    {
      onSuccess: () => {
        refetchStripeSetting()
      },
    }
  )

  // Add mutation for purging static config cache
  const { mutateAsync: purgeStaticConfigCache } =
    trpc.admin.cache.purgeStaticConfigCache.useMutation()

  if (!application) {
    return null
  }

  const dashboardWeb2MigrationEnabled = enabled("dashboard_web2_migration", application)
  const dashboardWeb2Url = buildDashboardWeb2ApplicationUrl({
    applicationId,
    organizationId: application.organizationId,
  })

  const getFeatureLabel = (feature: string) => {
    if (feature === "web_2_app") {
      return "Web Checkout"
    }

    if (feature === "flows") {
      return "Flows"
    }

    if (feature === "dashboard_web2") {
      return "New Dashboard Experience"
    }

    if (feature === "editor_ai_chat") {
      return "Editor Agents"
    }

    return feature.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  const getFeatureLabelNode = (feature: string) => {
    const label = getFeatureLabel(feature)
    const blockedReason =
      feature === "editor_ai_chat" && !enabled("flows", application)
        ? "Needs Flows. Enable Flows first."
        : null

    if (feature !== "dashboard_web2") {
      if (!blockedReason) return label

      return (
        <span className="flex flex-col gap-1">
          <span>{label}</span>
          <span className="text-xs text-white/50">{blockedReason}</span>
        </span>
      )
    }

    return label
  }

  return (
    <LayoutModal
      title="Public Beta Features"
      footer={
        "These features are in beta and may be subject to changes or bugs. Enable them at your own discretion. Changes apply to this application."
      }
    >
      <div className="flex flex-col gap-3">
        {PublicBetaFeatures.map((feature) => (
          <div
            key={feature}
            className="flex flex-row items-center justify-between gap-2 pr-3"
          >
            <div
              className={feature === "dashboard_web2" ? "opacity-50" : ""}
            >
              <SField.Label margin="mb-0">{getFeatureLabelNode(feature)}</SField.Label>
            </div>
            {feature === "dashboard_web2" && dashboardWeb2MigrationEnabled && dashboardWeb2Url ? (
              <div className="overflow-hidden rounded-md border border-brand-primary/20 bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/15">
                <TryNewDashboardButton
                  onClick={() => {
                    window.location.href = dashboardWeb2Url
                  }}
                />
              </div>
            ) : (
              <Switch
                checked={enabled(feature as Feature, application) ?? false}
                disabled={
                  feature === "dashboard_web2" ||
                  (feature === "editor_ai_chat" && !enabled("flows", application))
                }
                onCheckedChange={async (value) => {
                  await toggleFeatureMutation.mutateAsync({
                    applicationId,
                    feature,
                    enabled: value,
                  })
                }}
              />
            )}
          </div>
        ))}
        {/* <div className="mt-6 pt-6 border-t border-white/10">
          <div className="flex flex-row items-center justify-between gap-2 pr-3">
            <SField.Label margin="mb-0">
              <span>
                Mobile-Optimized Stripe Checkout (
                <a
                  className="text-white/60 underline"
                  target="_blank"
                  href="https://docs.stripe.com/mobile/digital-goods/checkout"
                >
                  Stripe docs
                </a>
                )
              </span>
              {!hasStripeApplication && (
                <span className="text-xs text-white/60 block">
                  Add{" "}
                  <a
                    className="text-white/60 underline"
                    target="_blank"
                    href="https://superwall.com/docs/sdk/guides/web-checkout"
                  >
                    web checkout
                  </a>{" "}
                  to enable this feature
                </span>
              )}
            </SField.Label>
            <SToggle
              value={hasStripeApplication ? !!enableStripeMobileCheckout : false}
              onToggle={async (value) => {
                if (!hasStripeApplication) return

                await updateStripeSetting("enable_stripe_mobile_checkout", value ? true : false)
                // Purge static config cache for the Stripe application
                if (stripeApplicationId) {
                  try {
                    await purgeStaticConfigCache({ applicationId: stripeApplicationId })
                    console.log("Successfully purged static config cache for Stripe application")
                  } catch (error) {
                    console.error("Failed to purge static config cache:", error)
                  }
                }
              }}
              disabled={isLoadingStripeSetting || !hasStripeApplication}
            />
          </div>
        </div> */}
      </div>
    </LayoutModal>
  )
}

const ApplicationExperimentsPage: BlitzPage = () => {
  useEnsureSession()
  return (
    <>
      <ApplicationExperiments />
    </>
  )
}

ApplicationExperimentsPage.getLayout = (page) => (
  <ApplicationSettingsV2>{page}</ApplicationSettingsV2>
)

export default ApplicationExperimentsPage
